const chapter = {
  id: 'plant-growth-development',
  slug: 'plant-growth-development',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Plant Growth and Development',
        hindi: 'पादप वृद्धि और विकास',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Plant Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'growth-basics',
      title: {
        english: 'Growth — Characteristics & Phases',
        hindi: 'वृद्धि — विशेषताएँ और चरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Growth = irreversible permanent increase in size of an organ/its parts/individual cell.',
        hindi: 'वृद्धि = किसी अंग/उसके भागों/व्यक्तिगत कोशिका के आकार में अपरिवर्तनीय स्थायी वृद्धि।',
            },
            {
              english: '**Open form of growth** in plants — meristems retain capacity for unlimited division (unlike animals).',
        hindi: '**पौधों में विकास का खुला रूप** - विभज्योतक असीमित विभाजन की क्षमता बनाए रखते हैं (जानवरों के विपरीत)।',
            },
            {
              english: 'Three phases at the cellular level:',
        hindi: 'सेलुलर स्तर पर तीन चरण:',
            },
            {
              english: '• **Meristematic** — apex; thin walls, dense protoplasm, large nuclei.',
        hindi: '• **मेरिस्टेमेटिक** — शीर्ष; पतली दीवारें, सघन जीवद्रव्य, बड़े नाभिक।',
            },
            {
              english: '• **Elongation** — increased vacuolation, cell enlargement, new wall deposition.',
        hindi: '• **विस्तार** - बढ़ी हुई रिक्तिका, कोशिका वृद्धि, नई दीवार का जमाव।',
            },
            {
              english: '• **Maturation** — cells attain final size, wall thickening, protoplasm modification.',
        hindi: '• **परिपक्वता** - कोशिकाएं अंतिम आकार प्राप्त करती हैं, दीवार मोटी हो जाती है, प्रोटोप्लाज्म संशोधन होता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'growth-rates',
      title: {
        english: 'Growth Rates & Measurement',
        hindi: 'वृद्धि दर और मापन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Arithmetic growth** — only one daughter cell continues to divide; other differentiates.',
        hindi: '**अंकगणितीय वृद्धि** - केवल एक पुत्री कोशिका विभाजित होती रहती है; अन्य भेद.',
            },
          ],
        },
        {
          type: 'equation',
          latex: 'L_t = L_0 + r\\,t',
          label: {
            english: 'Arithmetic — straight line on L vs t',
        hindi: 'अंकगणित - एल बनाम टी पर सीधी रेखा',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Geometric growth** — both daughter cells divide; exponential phase followed by stationary phase → **sigmoid (S-shape) curve** is typical of growth in higher plants & populations.',
        hindi: '**ज्यामितीय वृद्धि** - दोनों संतति कोशिकाएँ विभाजित होती हैं; घातीय चरण के बाद स्थिर चरण → **सिग्मॉइड (एस-आकार) वक्र** उच्च पौधों और आबादी में वृद्धि का विशिष्ट है।',
            },
          ],
        },
        {
          type: 'equation',
          latex: 'W_1 = W_0\\,e^{r t}',
          label: {
            english: 'Geometric/exponential; r = relative growth rate',
        hindi: 'ज्यामितीय/घातांकीय; आर = सापेक्ष विकास दर',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Absolute Growth Rate (AGR)** — total growth per unit time.',
        hindi: '**पूर्ण विकास दर (एजीआर)** - प्रति इकाई समय में कुल वृद्धि।',
            },
            {
              english: '**Relative Growth Rate (RGR)** — growth per unit time per unit initial value.',
        hindi: '**सापेक्ष विकास दर (आरजीआर)** - प्रति इकाई समय प्रति इकाई प्रारंभिक मूल्य में वृद्धि।',
            },
            {
              english: 'Two leaves with same AGR can have very different RGR if initial sizes differ.',
        hindi: 'यदि प्रारंभिक आकार भिन्न हो तो समान एजीआर वाली दो पत्तियों में बहुत भिन्न आरजीआर हो सकता है।',
            },
            {
              english: 'Conditions for growth — **water (turgidity), oxygen, nutrients, optimum T & light**.',
        hindi: 'वृद्धि के लिए परिस्थितियाँ - **पानी (गंदगी), ऑक्सीजन, पोषक तत्व, इष्टतम टी और प्रकाश**।',
            },
          ],
        },
      ],
    },
    {
      id: 'differentiation',
      title: {
        english: 'Differentiation, Dedifferentiation & Redifferentiation',
        hindi: 'विभेदन, विभेदन-प्रत्यावर्तन और पुनर्विभेदन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Differentiation** — cells from meristem & cambium mature to perform specific functions (e.g. tracheary elements lose protoplasm, develop wall thickenings).',
        hindi: '**विभेदन** - मेरिस्टेम और कैंबियम की कोशिकाएं विशिष्ट कार्य करने के लिए परिपक्व होती हैं (उदाहरण के लिए श्वासनली तत्व प्रोटोप्लाज्म खो देते हैं, दीवार मोटी हो जाती है)।',
            },
            {
              english: '**Dedifferentiation** — already differentiated cells regain capacity to divide (e.g. cork cambium, interfascicular cambium from parenchyma).',
        hindi: '**डिअवकलन** - पहले से ही विभेदित कोशिकाएं विभाजित होने की क्षमता हासिल कर लेती हैं (उदाहरण के लिए कॉर्क कैंबियम, पैरेन्काइमा से इंटरफैसिकुलर कैंबियम)।',
            },
            {
              english: '**Redifferentiation** — products of dedifferentiated tissues lose capacity to divide once again, become specialised (e.g. secondary cortex, secondary phloem).',
        hindi: '**पुनर्विभेदन** - विभेदित ऊतकों के उत्पाद एक बार फिर विभाजित होने की क्षमता खो देते हैं, विशिष्ट हो जाते हैं (उदाहरण के लिए द्वितीयक प्रांतस्था, द्वितीयक फ्लोएम)।',
            },
            {
              english: '**Plasticity** — different developmental pathways from same genotype → **heterophylly** (e.g. *Coriandrum, Larkspur, Cotton* — juvenile vs mature leaves; *Buttercup* — submerged vs aerial leaves).',
        hindi: '**प्लास्टिसिटी** - एक ही जीनोटाइप से विभिन्न विकासात्मक मार्ग → **हेटरोफिली** (उदाहरण के लिए *कोरियनड्रम, लार्क्सपुर, कॉटन* - किशोर बनाम परिपक्व पत्तियां; *बटरकप* - जलमग्न बनाम हवाई पत्तियां)।',
            },
          ],
        },
      ],
    },
    {
      id: 'pgrs-discovery',
      title: {
        english: 'Plant Growth Regulators (PGRs) — Discovery',
        hindi: 'पादप वृद्धि नियामक (PGR) — खोज',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Auxin** — Charles & Francis **Darwin** (Coleoptile of canary grass; phototropism, 1880) → **F.W. Went (1928)** isolated from oat coleoptile tip.',
        hindi: '**ऑक्सिन** - चार्ल्स और फ्रांसिस **डार्विन** (कैनरी घास का कोलोप्टाइल; फोटोट्रोपिज्म, 1880) → **एफ.डब्ल्यू. गया (1928)** ओट कोलोप्टाइल टिप से अलग किया गया।',
            },
            {
              english: '**Gibberellin (GA)** — discovered from rice "bakanae" (foolish seedling) disease caused by **Gibberella fujikuroi** (E. Kurosawa, 1926). > 100 GAs known; most studied = GA₃.',
        hindi: '**गिब्बरेलिन (जीए)** - चावल के "बकाने" (मूर्ख अंकुर) रोग से खोजा गया, जो **गिब्बरेला फुजिकुरोई** (ई. कुरोसावा, 1926) के कारण होता है। > 100 जीए ज्ञात; अधिकांश studied = GA₃.',
            },
            {
              english: '**Cytokinin** — F. Skoog & C. Miller from herring sperm DNA → **kinetin** (modified adenine). Natural = **zeatin** (from coconut milk + corn kernels).',
        hindi: '**साइटोकिनिन** - हेरिंग शुक्राणु डीएनए से एफ. स्कूग और सी. मिलर → **किनेटिन** (संशोधित एडेनिन)। Natural = **zeatin** (from coconut milk + corn kernels).',
            },
            {
              english: '**Ethylene** — gaseous; H.H. Cousins (1910) — orange ripening accelerated by gases from bananas.',
        hindi: '**एथिलीन** - गैसीय; एच.एच. कजिन्स (1910) - केले से निकलने वाली गैसों से संतरे का पकना तेज हो जाता है।',
            },
            {
              english: '**Abscisic Acid (ABA)** — discovered as inhibitor of abscission (Carns & Addicott).',
        hindi: '**एब्सिसिक एसिड (एबीए)** - एब्सिसिशन (कार्न्स एंड एडिकॉट) के अवरोधक के रूप में खोजा गया।',
            },
            {
              english: 'PGRs grouped into:',
        hindi: 'पीजीआर को इसमें समूहीकृत किया गया है:',
            },
            {
              english: '• **Promoters** — auxins, gibberellins, cytokinins.',
        hindi: '• **प्रवर्तक** - ऑक्सिन, जिबरेलिन, साइटोकिनिन।',
            },
            {
              english: '• **Inhibitors** — ABA, ethylene (also acts as promoter in fruit ripening).',
        hindi: '• **अवरोधक** - एबीए, एथिलीन (फलों को पकाने में प्रमोटर के रूप में भी कार्य करता है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'pgr-functions',
      title: {
        english: 'PGRs — Physiological Effects',
        hindi: 'PGR — शारीरिक प्रभाव',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Hormone', 'Major roles'],
          rows: [
            [
              'Auxin (IAA, IBA, NAA, 2,4-D)',
              'Apical dominance; root initiation; cell elongation; xylem differentiation; parthenocarpy (tomato); 2,4-D selective herbicide for dicot weeds',
            ],
            [
              'Gibberellin (GA₃)',
              '**Bolting** in beet/cabbage/long-day plants (rosette → elongated stem); ↑ length of stem, fruit (apple); breaks bud + seed dormancy; brewing — speeds malting in barley; ↑ length of sugarcane stem (yield ↑ 20 t/acre); early seed production in conifers',
            ],
            [
              'Cytokinin',
              'Promote cell division (cytokinesis); delay senescence (Richmond-Lang effect); break apical dominance → lateral bud growth; chloroplast formation',
            ],
            [
              'Ethylene (gas)',
              '**Climacteric ripening of fruits** (banana, mango, tomato); senescence + abscission of leaves/flowers/fruits; horizontal growth in seedlings (triple response); breaks dormancy in potato; promotes female flowers in cucurbits; root initiation. Source: **Ethephon** in solution.',
            ],
            [
              'ABA',
              'Stress hormone; closes stomata under water stress; induces seed dormancy; promotes abscission; antagonist to GA',
            ],
          ],
        },
      ],
    },
    {
      id: 'photoperiodism',
      title: {
        english: 'Photoperiodism & Vernalisation',
        hindi: 'प्रकाशावधिता और वसन्तीकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Photoperiodism** — response of plants to relative duration of light/dark (Garner & Allard, 1920 in tobacco "Maryland Mammoth").',
        hindi: '**फोटोपेरियोडिज्म** - light/dark की सापेक्ष अवधि के लिए पौधों की प्रतिक्रिया (गार्नर एंड एलार्ड, 1920 तम्बाकू "मैरीलैंड मैमथ" में)।',
            },
            {
              english: 'Three categories based on **critical day length (CDL)**:',
        hindi: '**महत्वपूर्ण दिन की लंबाई (सीडीएल)** पर आधारित तीन श्रेणियां:',
            },
            {
              english: '• **Long-day plants (LDP)** — flower when day length > CDL (wheat, barley, spinach, radish, henbane).',
        hindi: '• **लंबे दिन वाले पौधे (एलडीपी)** - दिन की लंबाई होने पर फूल > सीडीएल (गेहूं, जौ, पालक, मूली, हेनबैन)।',
            },
            {
              english: '• **Short-day plants (SDP)** — flower when day length < CDL (rice, soybean, *Xanthium*, chrysanthemum, dahlia, cocklebur).',
        hindi: '• **छोटे दिन के पौधे (एसडीपी)** - दिन की लंबाई <सीडीएल (चावल, सोयाबीन, *जैंथियम*, गुलदाउदी, डाहलिया, कॉकलेबर) पर फूल आते हैं।',
            },
            {
              english: '• **Day-neutral plants (DNP)** — independent of day length (tomato, cotton, sunflower, cucumber).',
        hindi: '• **दिन-तटस्थ पौधे (डीएनपी)** - दिन की लंबाई से स्वतंत्र (टमाटर, कपास, सूरजमुखी, ककड़ी)।',
            },
            {
              english: '**Critical factor is dark period** (continuous), NOT the light period. Interrupting the dark with brief flash of red light prevents SDP from flowering.',
        hindi: '**महत्वपूर्ण कारक अंधकार काल** (निरंतर) है, प्रकाश काल नहीं। लाल रोशनी की संक्षिप्त चमक के साथ अंधेरे को बाधित करना एसडीपी को फूलने से रोकता है।',
            },
            {
              english: 'Site of perception = **leaves**; pigment involved = **phytochrome (Pr ↔ Pfr)**; floral signal once postulated as "florigen" — now known to be FT protein.',
        hindi: 'perception = **leaves** की साइट; वर्णक involved = **phytochrome (Pr ↔ Pfr)**; पुष्प संकेत को एक बार "फ्लोरिजेन" के रूप में प्रतिपादित किया गया था - जिसे अब एफटी प्रोटीन के रूप में जाना जाता है।',
            },
            {
              english: '**Vernalisation** — promotion of flowering by exposure to low temperature (winter wheat sown in spring needs cold; biennials like cabbage, sugar beet, henbane). Site of perception = shoot apex / dividing cells. Reverses by heat = **devernalisation**.',
        hindi: '**वर्नलाइजेशन** - कम तापमान के संपर्क में आने से फूलों को बढ़ावा देना (वसंत में बोए जाने वाले शीतकालीन गेहूं को ठंड की जरूरत होती है; गोभी, चुकंदर, हेनबैन जैसे द्विवार्षिक गेहूं)। perception = shoot apex / dividing cells की साइट. heat = **devernalisation** द्वारा उलटा।',
            },
          ],
        },
      ],
    },
    {
      id: 'seed-dormancy',
      title: {
        english: 'Seed Dormancy',
        hindi: 'बीज प्रसुप्तावस्था',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Causes — impermeable / hard seed coat; chemical inhibitors (ABA, phenolic acids, *para*-ascorbic acid); immature embryo.',
        hindi: 'कारण - अभेद्य/कठोर बीज आवरण; रासायनिक अवरोधक (एबीए, फेनोलिक एसिड, *पैरा*-एस्कॉर्बिक एसिड); अपरिपक्व भ्रूण.',
            },
            {
              english: 'Methods to overcome — **scarification** (mechanical / chemical / fire), **stratification** (chilling), washing in water (leaches inhibitors), GA₃ treatment, change in environmental conditions (light, T).',
        hindi: 'काबू पाने के तरीके - **स्कारीकरण** (यांत्रिक/रासायनिक/अग्नि), **स्तरीकरण** (ठंडा करना), पानी में धोना (लीच अवरोधक), जीए₃ उपचार, पर्यावरणीय परिस्थितियों में बदलाव (प्रकाश, टी)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'L_t = L_0 + r\\,t \\quad (\\text{arithmetic, linear})',
      desc: {
        english: '',
        hindi: 'एल टी = एल 0 + आर\\,टी (अंकगणित, रैखिक)',
      },
    },
    {
      latex: 'W_1 = W_0\\,e^{r t} \\quad (\\text{geometric/sigmoid})',
      desc: {
        english: '',
        hindi: 'W 1 = W 0\\,e r t (ज्यामितीय/सिग्मॉइड)',
      },
    },
    {
      latex: '\\text{Auxin} \\rightarrow \\text{apical dominance, rooting}',
      desc: {
        english: '',
        hindi: 'औक्सिन शिखर प्रभुत्व, जड़',
      },
    },
    {
      latex: '\\text{GA}_3 \\rightarrow \\text{bolting, breaks dormancy}',
      desc: {
        english: '',
        hindi: 'जीए 3 बोल्टिंग, सुप्तता को तोड़ता है',
      },
    },
    {
      latex: '\\text{Cytokinin} \\rightarrow \\text{cell division, delays senescence}',
      desc: {
        english: '',
        hindi: 'साइटोकिनिन कोशिका विभाजन, बुढ़ापे में देरी करता है',
      },
    },
    {
      latex: '\\text{Ethylene} \\rightarrow \\text{climacteric ripening (banana, mango)}',
      desc: {
        english: 'Source: Ethephon',
        hindi: 'स्रोत: एथेफॉन',
      },
    },
    {
      latex: '\\text{ABA} \\rightarrow \\text{stress hormone, closes stomata}',
      desc: {
        english: '',
        hindi: 'एबीए तनाव हार्मोन, रंध्रों को बंद कर देता है',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Hormone',
      problem: {
        english: 'Bolting in beet/cabbage is induced by:',
        hindi: 'beet/cabbage में बोल्टिंग किसके द्वारा प्रेरित होती है:',
      },
      answer: {
        english: 'Gibberellin (GA₃)',
        hindi: 'गिबरेलिन (GA₃)',
      },
      solution: {
        english: 'Sudden internodal elongation; rosette → tall stem.',
        hindi: 'अचानक इंटरनोडल बढ़ाव; रोसेट → लंबा तना।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Photoperiod',
      problem: {
        english: 'Site of perception of light in photoperiodism is:',
        hindi: 'फोटोपेरियोडिज्म में प्रकाश की धारणा का स्थल है:',
      },
      answer: {
        english: 'Leaves (via phytochrome)',
        hindi: 'पत्तियां (फाइटोक्रोम के माध्यम से)',
      },
      solution: {
        english: 'Floral stimulus produced in leaves; transmitted to shoot apex.',
        hindi: 'पत्तियों में उत्पन्न पुष्प उत्तेजना; शूट एपेक्स को प्रेषित।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Ripening',
      problem: {
        english: 'Hormone responsible for climacteric fruit ripening is:',
        hindi: 'मौसमी फलों के पकने के लिए जिम्मेदार हार्मोन है:',
      },
      answer: {
        english: 'Ethylene',
        hindi: 'एथिलीन',
      },
      solution: {
        english: 'Commercially applied as Ethephon (2-chloroethylphosphonic acid).',
        hindi: 'व्यावसायिक रूप से एथेफॉन (2-क्लोरोएथिलफॉस्फोनिक एसिड) के रूप में उपयोग किया जाता है।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Cytokinin',
      problem: {
        english: 'Natural cytokinin found in coconut milk is:',
        hindi: 'नारियल के दूध में पाया जाने वाला प्राकृतिक साइटोकिनिन है:',
      },
      answer: {
        english: 'Zeatin',
        hindi: 'ज़ेटिन',
      },
      solution: {
        english: 'Kinetin = synthetic; zeatin = natural.',
        hindi: 'काइनेटिन = सिंथेटिक; ज़ेतिन = प्राकृतिक.',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'Vernalisation',
      problem: {
        english: 'Vernalisation refers to promotion of flowering by:',
        hindi: 'वर्नालाइज़ेशन का तात्पर्य फूलों को बढ़ावा देने से है:',
      },
      answer: {
        english: 'Exposure to low temperature',
        hindi: 'कम तापमान के संपर्क में आना',
      },
      solution: {
        english: 'Required by winter wheat, biennials.',
        hindi: 'शीतकालीन गेहूं, द्विवार्षिक के लिए आवश्यक।',
      },
    },
  ],
  next: {
    slug: 'biotech-principles-processes',
    title: 'Biotechnology — Principles and Processes',
  },
};

export default chapter;
